import { useId } from "react";
import type { OpcjaWyboru } from "../ui/Choice.js";
import { RadioGroup } from "../ui/Choice.js";
import { Field } from "../ui/Field.js";
import type { OpcjaSelect } from "../ui/Select.js";
import { Select } from "../ui/Select.js";
import type { ProviderInfoDto } from "../../lib/api.js";

interface Props {
  legend: string;
  name: string;
  providers: ProviderInfoDto[];
  value: string;
  onChange: (id: string) => void;
  /** Wybrana wersja modelu. Pusty string znaczy "domyslna dostawcy". */
  model: string;
  /**
   * Wywolywane TYLKO przy zmianie wersji.
   *
   * Zmiana dostawcy jej NIE wola, choc wersja musi wtedy wrocic do domyslnej -
   * wersja nalezy do dostawcy, wiec stara wartosc bylaby nazwa modelu, ktorego nowy
   * dostawca nie zna. Zerowanie robi wywolujacy, w tym samym kroku co `onChange`.
   * Gdyby robil to ten komponent, ustawienia zapisywalyby sie DWA razy na jedno
   * klikniecie (dwa zadania PUT i dwa toasty potwierdzenia).
   */
  onModelChange: (model: string) => void;
  /** Dodaje opcje wylaczajaca cala warstwe. */
  allowNone?: boolean;
  /**
   * Etykieta i opis opcji wylaczajacej.
   *
   * Sparametryzowane, bo od 23.08.2026 sa TRZY warstwy i kazda wylacza sie w co
   * innego. Zaszyte "Bez oceny brzmienia" wygladalo poprawnie, dopoki warstwa
   * byla jedna - przy trzech kazda mowilaby to samo o czym innym.
   */
  noneLabel?: string;
  noneNote?: string;
  disabled?: boolean;
  /**
   * Opisy wersji, gdy sama nazwa nie wystarcza do wyboru. Dzis wypelnia to
   * wylacznie warstwa sygnalowa: licencja rozstrzyga, czy modelu wolno uzyc
   * komercyjnie, a z nazwy tego nie widac.
   */
  modelInfo?: { id: string; license: string; commercialUse: boolean; note: string }[];
}

/**
 * Wybor dostawcy i wersji modelu.
 *
 * F-8: dostawca to grupa radio na prymitywie RadioGroup. Wczesniej byly tu gole
 * <input type="radio"> bez zadnego CSS - natywna kropka przegladarki, obszar
 * klikalny znacznie ponizej 38px z A-3 i zero focus ringu z A-1.
 * F-6: wersja modelu to Select DS, nie natywny <select> (natywnej listy nie da sie
 * ostylowac, wiec rozwinieta wygladala jak bebech systemu).
 * F-1: wersja ma etykiete NAD polem i hint POD nim.
 *
 * Dostawcy bez klucza sa POKAZYWANI, ale zablokowani, z powodem w opisie opcji
 * (S-10: wylaczone zawsze Z POWODEM). Ukrycie ich byloby wygodniejsze w kodzie
 * i gorsze dla uzytkownika: nie wiedzialby, ze opcja istnieje i czego brakuje,
 * zeby ja wlaczyc.
 *
 * Lista wersji pojawia sie tylko pod WYBRANYM dostawca. Pokazywanie jej przy
 * kazdym daloby cztery listy rozwijane na ekranie, z ktorych trzy nic nie robia.
 *
 * ZGLOSZENIE (PB-5): `Select` nie ma stanu wylaczonego, wiec `disabled` gasi tu
 * tylko grupe radio. Prymitywu nie ruszamy - poprawka zmienilaby wyglad widokow
 * sasiada.
 */
export function ProviderPicker({
  legend,
  name,
  providers,
  value,
  onChange,
  model,
  onModelChange,
  allowNone = false,
  noneLabel,
  noneNote,
  modelInfo,
  disabled = false,
}: Props): React.JSX.Element {
  const baza = useId();
  const idWersji = `${baza}-wersja`;
  const wybrany = providers.find((p) => p.id === value);

  const opcje: OpcjaWyboru[] = providers.map((p) => ({
    wartosc: p.id,
    etykieta: p.label,
    // S-10: powod niedostepnosci stoi przy opcji, a nie w osobnym komunikacie -
    // inaczej trzeba go szukac po ekranie.
    opis: p.available ? p.note : `Niedostępny: ${p.unavailableReason ?? "brak konfiguracji"}. ${p.note}`,
    wylaczona: disabled || !p.available,
  }));

  if (allowNone) {
    opcje.push({
      wartosc: "brak",
      etykieta: noneLabel ?? "Bez oceny brzmienia",
      opis:
        noneNote ??
        "Sama prozodia: tempo, ton i pauzy. Adnotacja będzie miała tylko część zmierzoną.",
      wylaczona: disabled,
    });
  }

  // Licencja przy wersji, gdy dostawca ja poda. Bez tego model, ktorego nie
  // wolno sprzedac, wyglada na liscie identycznie jak ten, ktory wolno - a to
  // jest roznica, ktora widac dopiero u prawnika.
  const opisWersji = (m: string): string => {
    const info = modelInfo?.find((i) => i.id === m);
    if (!info) return m;
    return `${m} - ${info.license}${info.commercialUse ? "" : ", BEZ prawa do użytku komercyjnego"}`;
  };

  const wersje: OpcjaSelect[] = wybrany
    ? [
        { wartosc: "", etykieta: `Domyślna (${wybrany.model})` },
        ...wybrany.models.map((m) => ({ wartosc: m, etykieta: opisWersji(m) })),
      ]
    : [];

  return (
    <>
      <RadioGroup etykieta={legend} nazwa={name} wartosc={value} opcje={opcje} onZmiana={onChange} />

      {wybrany && wybrany.models.length > 0 && (
        <Field
          etykieta={`Wersja modelu: ${wybrany.label}`}
          id={idWersji}
          hint="Domyślna wersja wystarcza w większości rozmów. Inną wybierz, gdy chcesz porównać wyniki."
          waskie
        >
          {(atrybuty) => (
            <Select {...atrybuty} wartosc={model} opcje={wersje} onZmiana={onModelChange} placeholder="Domyślna" />
          )}
        </Field>
      )}
    </>
  );
}
